import { License } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import SuccessIcon from '@swf/core/assets/icons/success-deposit.svg';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import BonusIcon from '../../../../../../assets/icons/bonus.svg';
import Alert from '../../../../../../components/Alert/Alert';
import { PaymentVerificationTypes } from '../../../../../../types/Cashier';
import type {
  BankWireResultInfo,
  DepositContext,
  DepositResult
} from '../../../../../../types/Cashier';
import {
  calculateCashBonusAmount,
  convertAmountByRate,
  isZeroExponentCurrency
} from '../../../../../../utils/cashier';
import { calculateFee } from '../../../../../../utils/fee';
import TransitionalExitPrompt from '../../../../../TransitionalExitPrompt/TransitionalExitPrompt';
import { isBankTransfer } from '../../../../data';
import PaymentMethodDetailsSummary from '../../../PaymentMethodDetailsSummary/PaymentMethodDetailsSummary';
import BankTransfer from '../BankTransfer/BankTransfer';
import MoreMoney from '../MoreMoney/MoreMoney';

import {
  TXTWCL_A_N0_commission_has_been_deduc_ELL,
  TXTWCL_Bonus_applied,
  TXTWCL_Just_so_you_know,
  TXTWCL_N0_Commission,
  TXTWCL_Verify_your_identity_to_keep_y_ELL,
  TXTWCL_Verify_your_identity_within_N0_ELL,
  TXTWCL_View_bonus_info,
  TXTWCL_WeXll_email_you_as_soon_as_you_ELL_2,
  TXTWCL_YouXre_currently_using_an_unve_ELL_1,
  TXTWCL_YouXre_currently_using_an_unve_ELL_2,
  TXTWCL_YouXre_currently_using_an_unve_ELL_3,
  TXTWCL_YouXre_currently_using_an_unve_ELL_5,
  TXTWCL_You_are_unable_to_withdraw_any_ELL,
  TXTWCL_You_can_continue_to_make_depos_ELL,
  TXTWCL_Your_N0_deposit_is_ready_to_pl_ELL,
  TXTWCL_Your_N0_deposit_was_successful
} from './Completed.messages.json';
import {
  alert,
  alertTitle,
  bonusIcon,
  container,
  icon,
  main,
  text,
  wrapper
} from './Completed.scss';

type Props = DepositResult &
  DepositContext & {
    cmsHeaderAndGridLayout: boolean;
    showPrompt?: boolean;
  };

const Completed: React.FC<Props> = ({
  previousDeposit,
  deposit,
  resultInfo,
  cmsHeaderAndGridLayout,
  showPrompt
}) => {
  const exitWizard = useWizardExit();
  const [rateChanged, setRateChanged] = useState(!!previousDeposit);
  const { amount, rate, balanceCurrency, type } = deposit;
  const license = useSelector(select.account.getLicense);
  const isLimitedAccount = useSelector(select.account.isLimitedAccount);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const { modal } = useDispatch<Dispatch>();
  const displayAmount = Number(convertAmountByRate(amount, rate, balanceCurrency, false));
  const {
    data: { paySystemDetails, depositResultContext, preCashinInfo },
    values: { paymentVerification }
  } = useWizardContext();
  const { formatNumber } = useIntl();

  const { paysysFee: paySystemFee } = paySystemDetails || {};

  const commission = paySystemFee?.rate_x_100K / 1000;
  const fee = calculateFee(displayAmount, paySystemFee);
  const displayAmountAfterFee = displayAmount - fee;

  const bonusAmount =
    depositResultContext?.bonusAmount ||
    (preCashinInfo && calculateCashBonusAmount(preCashinInfo, formatNumber));

  const handleLink = () => {
    if (isEmbedded) {
      window.starsweb?.app.execute('Bonuses');
      exitWizard();
      return;
    }
    !cmsHeaderAndGridLayout && exitWizard();
    modal.enqueue({ path: '/account/my-stars/bonus/bonuses' });
  };

  return (
    <>
      {rateChanged && (
        <MoreMoney
          previousDeposit={previousDeposit}
          deposit={deposit}
          onClose={() => setRateChanged(false)}
        />
      )}
      {!isBankTransfer(type) && (
        <>
          <div className={wrapper}>
            <SuccessIcon className={icon} />
            <Translation
              tagName="h2"
              {...TXTWCL_Your_N0_deposit_was_successful}
              values={{
                0: (
                  <CurrencyAmount
                    value={displayAmount}
                    isZeroExponentCurrency={isZeroExponentCurrency(balanceCurrency)}
                    currency={balanceCurrency}
                    dollarValue
                  />
                )
              }}
            />
          </div>
          <PaymentMethodDetailsSummary type={Number(type)} variant="result" depositSuccess />
          {license === License.RO && fee > 0 && (
            <Alert variant="info" className={alert}>
              <div className={alertTitle}>
                <Translation {...TXTWCL_N0_Commission} values={{ 0: `${commission}%` }} />
              </div>
              <Translation
                tagName="span"
                {...TXTWCL_A_N0_commission_has_been_deduc_ELL}
                values={{
                  0: `${commission}%`,
                  1: (
                    <CurrencyAmount
                      value={displayAmountAfterFee}
                      isZeroExponentCurrency={isZeroExponentCurrency(balanceCurrency)}
                      currency={balanceCurrency}
                      dollarValue
                    />
                  )
                }}
              />
            </Alert>
          )}
          {!isEmpty(deposit.bonusCode) && (
            <>
              <div className={container}>
                <div className={bonusIcon}>
                  <BonusIcon />
                </div>
                <div className={main}>
                  <Translation tagName="div" {...TXTWCL_Bonus_applied} />
                </div>
                {bonusAmount && <div className={main}>{bonusAmount}</div>}
              </div>
              <div className={text}>
                <Translation
                  {...TXTWCL_Your_N0_deposit_is_ready_to_pl_ELL}
                  values={{
                    0: (
                      <CurrencyAmount
                        value={Number(convertAmountByRate(amount, rate, balanceCurrency, false))}
                        isZeroExponentCurrency={isZeroExponentCurrency(balanceCurrency)}
                        currency={balanceCurrency}
                        dollarValue
                      />
                    )
                  }}
                />{' '}
                <Translation {...TXTWCL_WeXll_email_you_as_soon_as_you_ELL_2} />
                <StarsLink onClick={handleLink}>
                  <Translation {...TXTWCL_View_bonus_info} />
                </StarsLink>
              </div>
            </>
          )}
          {isLimitedAccount && (
            <Alert variant="info" className={alert}>
              <div className={alertTitle}>
                <Translation {...TXTWCL_Just_so_you_know} />
              </div>
              {license === License.RO ? (
                <Translation {...TXTWCL_YouXre_currently_using_an_unve_ELL_1} />
              ) : [License.IT, License.ES, License.FR].includes(license) ? (
                <Translation {...TXTWCL_YouXre_currently_using_an_unve_ELL_2} />
              ) : license === License.CZ ? (
                <Translation {...TXTWCL_YouXre_currently_using_an_unve_ELL_3} />
              ) : (
                ''
              )}{' '}
              {license === License.ES ? (
                <Translation {...TXTWCL_Verify_your_identity_to_keep_y_ELL} />
              ) : (
                <Translation
                  {...TXTWCL_Verify_your_identity_within_N0_ELL}
                  values={{ 0: license !== License.IT ? 30 : 60 }}
                />
              )}
            </Alert>
          )}
          {paymentVerification?.menuSelected === PaymentVerificationTypes.verifyLater && (
            <Alert variant="info" className={alert}>
              <div className={alertTitle}>
                <Translation {...TXTWCL_Just_so_you_know} />
              </div>
              <Translation {...TXTWCL_YouXre_currently_using_an_unve_ELL_5} />{' '}
              {license === License.DE ? (
                <Translation {...TXTWCL_You_are_unable_to_withdraw_any_ELL} />
              ) : (
                <Translation {...TXTWCL_You_can_continue_to_make_depos_ELL} />
              )}
            </Alert>
          )}
        </>
      )}
      {showPrompt && <TransitionalExitPrompt />}
      {isBankTransfer(type) && (
        <BankTransfer deposit={deposit} resultInfo={resultInfo as BankWireResultInfo} />
      )}
    </>
  );
};

export default Completed;
